import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogIn, LogOut } from 'lucide-react';
import type { TodaySessionDto } from '@fas/types';
import { Badge, Button, Dialog, DialogContent } from '@fas/ui';
import { formatDate } from '@/lib/format';
import { cachedToday, currentSkewMs, loadOfflineQueue, useOfflineQueue } from '@/lib/offline-queue';
import { ClockDialog, sessionTitle } from './ClockDialog';
import { clockRange } from './labels';

type Target = { session: TodaySessionDto; kind: 'in' | 'out' } | null;

function windowOpen(s: TodaySessionDto, serverNow: number): boolean {
  if (!s.actions.clockInOpensAt || !s.actions.clockInClosesAt) return false;
  return serverNow >= Date.parse(s.actions.clockInOpensAt) && serverNow < Date.parse(s.actions.clockInClosesAt);
}

/**
 * Recording attendance when the app starts without a connection (§57, Phase 2D): the day's
 * sessions as last seen online on this device, with clock actions that save the event on the
 * device. Nothing is recorded until the owner is back online and signed in.
 */
export function OfflineAttendance() {
  const cached = useQuery({ queryKey: ['offline', 'today'], queryFn: cachedToday, staleTime: Infinity, retry: false });
  const events = useOfflineQueue((s) => s.events);
  const [target, setTarget] = useState<Target>(null);
  useEffect(() => {
    void loadOfflineQueue();
  }, []);
  if (!cached.data) return null;
  const { data, userId, userName } = cached.data;
  const serverNow = Date.now() + currentSkewMs();
  const sessions = [...data.daily, ...data.classes].filter((s) => s.requiresClocking && s.actions.offlineCapture);
  if (sessions.length === 0) return null;

  return (
    <section aria-labelledby="offline-attendance" className="mt-8 rounded-lg border border-slate-200 bg-white p-4 text-left">
      <h2 id="offline-attendance" className="font-semibold">
        Record attendance without a connection
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        {userName} · {formatDate(data.date)}. Records stay on this device until you are back online and signed in; then they are sent and checked by a reviewer.
      </p>
      <ul className="mt-3 divide-y divide-slate-100">
        {sessions.map((s) => {
          const mine = events.filter((e) => e.sessionId === s.id && e.userId === userId);
          const savedIn = mine.some((e) => e.kind === 'CLOCK_IN');
          const savedOut = mine.some((e) => e.kind === 'CLOCK_OUT');
          const canIn = !savedIn && s.attendanceStatus === 'SCHEDULED' && windowOpen(s, serverNow);
          const canOut = !savedOut && (s.attendanceStatus === 'CLOCKED_IN' || savedIn);
          return (
            <li key={s.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm">
                <p className="font-medium">{sessionTitle(s)}</p>
                <p className="text-slate-600">{clockRange(s.scheduledStart, s.scheduledEnd, data.timeZone)}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {savedIn ? <Badge tone="warning">Clock-in saved on this device</Badge> : null}
                  {savedOut ? <Badge tone="warning">Clock-out saved on this device</Badge> : null}
                </div>
              </div>
              {canIn ? (
                <Button onClick={() => setTarget({ session: s, kind: 'in' })}>
                  <LogIn aria-hidden /> {s.sessionType === 'DAILY' ? 'Time in' : 'Clock in'}
                </Button>
              ) : canOut ? (
                <Button onClick={() => setTarget({ session: s, kind: 'out' })}>
                  <LogOut aria-hidden /> {s.sessionType === 'DAILY' ? 'Time out' : 'Clock out'}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <Dialog open={target !== null} onOpenChange={(open) => (open ? null : setTarget(null))}>
        {target ? (
          <DialogContent title={`${target.kind === 'in' ? 'Clock in' : 'Clock out'} without a connection`} size="lg">
            <ClockDialog session={target.session} kind={target.kind} timeZone={data.timeZone} offlineOnly offlineUserId={userId} onDone={() => undefined} onClose={() => setTarget(null)} />
          </DialogContent>
        ) : null}
      </Dialog>
    </section>
  );
}
